import { Section } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import {
  HOME_EQUITY_TURBO_WELCOME_CTA,
  HOME_EQUITY_TURBO_WELCOME_PERSONAL_INFO,
  HOME_EQUITY_TURBO_WELCOME_CITIZENSHIP,
  HOME_EQUITY_TURBO_WELCOME_TAX_INS_FEE,
  HOME_EQUITY_TURBO_WELCOME_PRIMARY_HOME,
  HOME_EQUITY_TURBO_WELCOME_COBORROWER,
  HOME_EQUITY_TURBO_WELCOME_COBORROWER_ADDRESS,
  HOME_EQUITY_TURBO_WELCOME_CREDIT_REPORT_ONE,
  HOME_EQUITY_TURBO_WELCOME_REAL_ESTATE_LIABILITIES,
  HOME_EQUITY_TURBO_WELCOME_REO_PRIMARY_QUESTIONS,
  HOME_EQUITY_TURBO_WELCOME_REO_SECONDARY_QUESTIONS,
  HOME_EQUITY_TURBO_WELCOME_ADDITIONAL_PROPERTIES,
  HOME_EQUITY_TURBO_WELCOME_AUTOMATIC_VOIE,
  HOME_EQUITY_TURBO_WELCOME_COLLECT_SSN,
  HOME_EQUITY_TURBO_WELCOME_VERIFICATION_CONNECT,
  HOME_EQUITY_TURBO_WELCOME_VOIE_REPORTS,
  HOME_EQUITY_TURBO_WELCOME_EMPLOYMENT_TYPE,
  HOME_EQUITY_TURBO_WELCOME_EMPLOYEE_INFO,
  HOME_EQUITY_TURBO_WELCOME_RETIRED_INFO,
  HOME_EQUITY_TURBO_WELCOME_COBORROWER_EMPLOYMENT_TYPE,
  HOME_EQUITY_TURBO_WELCOME_COBORROWER_EMPLOYEE_INFO,
  HOME_EQUITY_TURBO_WELCOME_BORROWER_DECLARATION,
  HOME_EQUITY_TURBO_WELCOME_COBORROWER_DECLARATION,
  HOME_EQUITY_TURBO_WELCOME_COLLECT_SSN_FINAL,
  HOME_EQUITY_TURBO_WELCOME_UPLOAD_DOCUMENTS,
  HOME_EQUITY_TURBO_WELCOME_COLLECT_SSN_QUESTION,
  HOME_EQUITY_TURBO_WELCOME_COLLECT_SSN_QUESTION_FINAL,
  HOME_EQUITY_MINIMUM_LOAN_STOP_GATE,
} from 'app/models/sections/constants';
import { homeEquityTurboWelcomeQuestionsByName } from 'app/models/questions/home-equity-turbo-welcome';
import { ContactWelcome } from 'app/components/ContactWelcome';
import { handleGoToFinancialVerificationPreStep, handleHomeEquityTurboFormSubmit, handleTurboHelocOnlyRates, handleTurboHomeEquityWelcomeGetFinalRates, handleTurboWelcomeGoToEmployment } from 'app/actions/form/turbo/actions';
import { canVerifyOfIncomeEmployment, coBorrowerHasDifferentAddress, hasCoBorrower, hasEnabledCreditReportOnTurbo, isCoBorrowerEmployee, isCoBorrowerEmploymentOther, isCoBorrowerRetired, isCoBorrowerSelfEmployed, isEmployee, isEmploymentOther, isExcludingSsnQuestions, isManualIncomeAndEmploymentRequired, isPrimaryHome, isRetired, isSelfEmployed, LoanOfficerCondition } from 'app/models/fields/conditionals';
import { ajaxValidateBorrowerResidence, ajaxValidateCoBorrowerResidence, updateHomeEquityTurboAddress } from 'app/actions/form/address-validation/actions';
import { AuthorizeCreditReport } from 'app/components/FormAgreements/AuthorizeCreditReport';
import { FormParagraphs } from 'app/models/paragraphs';
import { ajaxGetFinancialVerificationUrl } from 'app/actions/form/verification/actions';
import { AuthorizeSsnFullPull } from 'app/components/FormAgreements/AuthorizeSsnFullPull';
import { getTurboDeclarationQuestions } from 'app/models/questions/declarations';
import { Disclosure } from 'app/components/Disclaimer/Disclosure';
import { BorrowerType } from '@lenderful/domain';
import { UploadDocumentsStep } from 'app/components/Turbo/UploadDocumentsStep';
import { MinimumLoanAmount } from 'app/components/StopGates/MinimumLoanAmount';
import { ajaxPostCreditReportForTurboWelcome } from 'app/actions/form/credit-report/actions';
import { homeEquityTurboWelcomeQuestionById } from 'app/models/questions/home-equity-turbo-welcome';

export const getHomeEquityTurboWelcomeSectionById = (id: number): Section => {
  return HOME_EQUITY_TURBO_WELCOME_SECTIONS.find((section) => section.id === id) as Section;
};

const HOME_EQUITY_TURBO_WELCOME_SECTIONS: Section[] = [
  {
    id              : HOME_EQUITY_TURBO_WELCOME_CTA,
    ifNoQuestions   : ContactWelcome,
    hideProgressBar : true,
    hideBackButton  : true,
    submitAction    : handleTurboHelocOnlyRates,
  },
  {
    id       : HOME_EQUITY_TURBO_WELCOME_PERSONAL_INFO,
    title    : 'Enter your personal information',
    questions: homeEquityTurboWelcomeQuestionsByName([
      FieldNames.firstName,
      FieldNames.middleName,
      FieldNames.lastName,
      FieldNames.suffixName,
      FieldNames.dateOfBirth,
      FieldNames.email,
      FieldNames.phone,
    ]),
    conditionalIf : {
      referToLoIf : [LoanOfficerCondition.isBorrowerUnderage],
    },
  },
  {
    id        : HOME_EQUITY_TURBO_WELCOME_CITIZENSHIP,
    questions : homeEquityTurboWelcomeQuestionsByName([
      FieldNames.citizenshipType,
      FieldNames.maritalStatus,
    ]),
    conditionalIf : {
      referToLoIf : [LoanOfficerCondition.isBorrowerNotCitizen],
    },
  },
  {
    id        : HOME_EQUITY_TURBO_WELCOME_TAX_INS_FEE,
    title     : 'What are the current taxes and insurance on the home you will use for collateral?',
    questions : homeEquityTurboWelcomeQuestionsByName([
      FieldNames.yearTaxes,
      FieldNames.yearInsure,
      FieldNames.yearAssociate,
    ]),
    submitAction: updateHomeEquityTurboAddress,
  },
  {
    id: HOME_EQUITY_TURBO_WELCOME_PRIMARY_HOME,
    title: 'Tell us about your primary home',
    questions: homeEquityTurboWelcomeQuestionsByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingPropertyType,
      FieldNames.livingYearTaxes,
      FieldNames.livingYearInsure,
      FieldNames.livingYearAssociate,
    ]),
    showIf: [(values) => !isPrimaryHome(values)],
    submitAction : (formValues) => ajaxValidateBorrowerResidence(formValues),
  },
  {
    id        : HOME_EQUITY_TURBO_WELCOME_COBORROWER,
    questions : homeEquityTurboWelcomeQuestionsByName([
      FieldNames.coBorrowerYN,
      FieldNames.coBorrowerFirstName,
      FieldNames.coBorrowerMiddleName,
      FieldNames.coBorrowerLastName,
      FieldNames.coBorrowerSuffixName,
      FieldNames.coBorrowerDOB,
      FieldNames.coBorrowerEmail,
      FieldNames.coBorrowerPhone,
      FieldNames.coBorrowerCitizenshipType,
    ]),
    conditionalIf : {
      referToLoIf : [LoanOfficerCondition.isCoBorrowerUnderage, LoanOfficerCondition.isCoBorrowerNotCitizen],
    },
  },
  {
    id        : HOME_EQUITY_TURBO_WELCOME_COBORROWER_ADDRESS,
    questions : homeEquityTurboWelcomeQuestionsByName([
      FieldNames.coBorrowerSameAddress,
      FieldNames.coBorrowerLivingPropertyStreet,
      FieldNames.coBorrowerLivingPropertyStreet2,
      FieldNames.coBorrowerLivingPropertyCity,
      FieldNames.coBorrowerLivingPropertyState,
      FieldNames.coBorrowerLivingPropertyZip,
      FieldNames.coBorrowerLivingRentOrOwn,
      FieldNames.coBorrowerMaritalStatus,
    ]),
    showIf       : [hasCoBorrower],
    submitIf     : [(formValues) => coBorrowerHasDifferentAddress(formValues)],
    submitAction : (formValues) => ajaxValidateCoBorrowerResidence(formValues),
  },
  {
    id           : HOME_EQUITY_TURBO_WELCOME_CREDIT_REPORT_ONE,
    questions    : homeEquityTurboWelcomeQuestionsByName([FieldNames.loanOfficer]),
    agreement    : <AuthorizeCreditReport />,
    showIf       : [(values) => hasEnabledCreditReportOnTurbo(values)],
    submitAction : ajaxPostCreditReportForTurboWelcome,
  },
  {
    id        : HOME_EQUITY_TURBO_WELCOME_REAL_ESTATE_LIABILITIES,
    title     : 'Real Estate Liabilities',
    questions : homeEquityTurboWelcomeQuestionsByName([FieldNames.realEstateLiabilities]),
    showIf    : [(values) => hasEnabledCreditReportOnTurbo(values)],
  },
  {
    id        : HOME_EQUITY_TURBO_WELCOME_REO_PRIMARY_QUESTIONS,
    title     : 'In addition to your Primary Home, how many other homes do you own?',
    questions : homeEquityTurboWelcomeQuestionsByName([
      FieldNames.liabilityCombinedNumberOtherHomes,
      FieldNames.liabilityCombinedYearTaxes,
      FieldNames.liabilityCombinedYearInsure,
      FieldNames.liabilityCombinedYearAssociate,
    ]),
    showIf: [(values) => isPrimaryHome(values) && hasEnabledCreditReportOnTurbo(values)],
    submitAction: handleTurboHomeEquityWelcomeGetFinalRates,
  },
  {
    id        : HOME_EQUITY_TURBO_WELCOME_REO_SECONDARY_QUESTIONS,
    questions : homeEquityTurboWelcomeQuestionsByName([
      FieldNames.isOwningOtherHomes,
      FieldNames.liabilityCombinedNumberOtherHomes,
      FieldNames.liabilityCombinedYearTaxes,
      FieldNames.liabilityCombinedYearInsure,
      FieldNames.liabilityCombinedYearAssociate,
    ]),
    showIf: [(values) => !isPrimaryHome(values) && hasEnabledCreditReportOnTurbo(values)],
    submitAction: handleTurboHomeEquityWelcomeGetFinalRates,
  },
  {
    id       : HOME_EQUITY_TURBO_WELCOME_ADDITIONAL_PROPERTIES,
    questions: homeEquityTurboWelcomeQuestionsByName([
      FieldNames.hasAddlPropertiesYN,
      FieldNames.addlProperties,
    ]),
    showIf: [(values) => !hasEnabledCreditReportOnTurbo(values)],
    submitAction: handleTurboWelcomeGoToEmployment,
  },
  {
    id           : HOME_EQUITY_TURBO_WELCOME_AUTOMATIC_VOIE,
    paragraph    : FormParagraphs.automaticVoie,
    questions    : homeEquityTurboWelcomeQuestionsByName([FieldNames.canVerifyOfIncomeEmployment]),
    title        : 'If you are a W2 employee, would you like to automatically receive verification of your income and employment?',
    submitAction : handleGoToFinancialVerificationPreStep,
  },
  {
    id            : HOME_EQUITY_TURBO_WELCOME_COLLECT_SSN,
    questions     : [homeEquityTurboWelcomeQuestionById(HOME_EQUITY_TURBO_WELCOME_COLLECT_SSN_QUESTION)],
    showIf        : [canVerifyOfIncomeEmployment],
    submitIf      : [canVerifyOfIncomeEmployment],
    submitAction  : ajaxGetFinancialVerificationUrl,
    agreement     : <AuthorizeSsnFullPull />,
  },
  {
    id                : HOME_EQUITY_TURBO_WELCOME_VERIFICATION_CONNECT,
    questions         : homeEquityTurboWelcomeQuestionsByName([FieldNames.isFinancialVerificationComplete]),
    showIf            : [canVerifyOfIncomeEmployment],
    hideBackButton    : true,
    hideForwardButton : true,
  },
  {
    id             : HOME_EQUITY_TURBO_WELCOME_VOIE_REPORTS,
    questions      : homeEquityTurboWelcomeQuestionsByName([FieldNames.isFinancialVerificationComplete]),
    showIf         : [canVerifyOfIncomeEmployment],
    hideBackButton : true,
  },
  {
    id        : HOME_EQUITY_TURBO_WELCOME_EMPLOYMENT_TYPE,
    questions : homeEquityTurboWelcomeQuestionsByName([FieldNames.employmentType]),
    showIf: [isManualIncomeAndEmploymentRequired],
  },
  {
    id       : HOME_EQUITY_TURBO_WELCOME_EMPLOYEE_INFO,
    title    : 'Tell us about your income and employment',
    questions: homeEquityTurboWelcomeQuestionsByName([
      FieldNames.employCompanyName,
      FieldNames.employStreet,
      FieldNames.employStreet2,
      FieldNames.employCity,
      FieldNames.employState,
      FieldNames.employZip,
      FieldNames.employTitle,
      FieldNames.employStart,
      FieldNames.employBase,
      FieldNames.employPrevName,
      FieldNames.employPrevStreet,
      FieldNames.employPrevStreet2,
      FieldNames.employPrevCity,
      FieldNames.employPrevState,
      FieldNames.employPrevZip,
      FieldNames.employPrevTitle,
      FieldNames.employPrevStart,
      FieldNames.employPrevEnd,
      FieldNames.employOvertime,
      FieldNames.employCommission,
      FieldNames.employBonus,
      FieldNames.additionalIncome,
    ]),
    showIf: [(values) => isManualIncomeAndEmploymentRequired(values) && (isEmployee(values) || isSelfEmployed(values))],
  },
  {
    id       : HOME_EQUITY_TURBO_WELCOME_RETIRED_INFO,
    questions: homeEquityTurboWelcomeQuestionsByName([
      FieldNames.retirementIncome,
      FieldNames.otherIncome,
    ]),
    showIf: [(values) => isManualIncomeAndEmploymentRequired(values) && (isRetired(values) || isEmploymentOther(values))],
  },
  {
    id       : HOME_EQUITY_TURBO_WELCOME_COBORROWER_EMPLOYMENT_TYPE,
    questions: homeEquityTurboWelcomeQuestionsByName([
      FieldNames.coBorrowerEmploymentType,
    ]),
    showIf: [(values) => isManualIncomeAndEmploymentRequired(values) && hasCoBorrower(values)],
  },
  {
    id       : HOME_EQUITY_TURBO_WELCOME_COBORROWER_EMPLOYEE_INFO,
    title    : 'Tell us about your co-borrower\'s income and employment',
    questions: homeEquityTurboWelcomeQuestionsByName([
      FieldNames.coBorrowerEmployCompanyName,
      FieldNames.coBorrowerEmployStreet,
      FieldNames.coBorrowerEmployStreet2,
      FieldNames.coBorrowerEmployCity,
      FieldNames.coBorrowerEmployState,
      FieldNames.coBorrowerEmployZip,
      FieldNames.coBorrowerEmployTitle,
      FieldNames.coBorrowerEmployStart,
      FieldNames.coBorrowerEmployBase,
      FieldNames.coBorrowerEmployPrevName,
      FieldNames.coBorrowerEmployPrevStreet,
      FieldNames.coBorrowerEmployPrevStreet2,
      FieldNames.coBorrowerEmployPrevCity,
      FieldNames.coBorrowerEmployPrevState,
      FieldNames.coBorrowerEmployPrevZip,
      FieldNames.coBorrowerEmployPrevTitle,
      FieldNames.coBorrowerEmployPrevStart,
      FieldNames.coBorrowerEmployPrevEnd,
      FieldNames.coBorrowerEmployOvertime,
      FieldNames.coBorrowerEmployCommission,
      FieldNames.coBorrowerEmployBonus,
      FieldNames.coBorrowerAdditionalIncome,
      FieldNames.coBorrowerOtherIncome,
      FieldNames.coBorrowerRetirementIncome,
    ]),
    showIf: [
      (values) => isManualIncomeAndEmploymentRequired(values) &&
      (isCoBorrowerEmployee(values) ||
        isCoBorrowerEmploymentOther(values) ||
        isCoBorrowerRetired(values) ||
        isCoBorrowerSelfEmployed(values)),
    ],
  },
  {
    id           : HOME_EQUITY_TURBO_WELCOME_BORROWER_DECLARATION,
    title        : 'Borrower Questions',
    questions    : getTurboDeclarationQuestions(BorrowerType.PRIMARY),
    submitIf     : [(values) => !hasCoBorrower(values) && isExcludingSsnQuestions(values)],
    submitAction : handleHomeEquityTurboFormSubmit,
    submitText   : 'Submit',
    agreement    : <Disclosure />,
  },
  {
    id           : HOME_EQUITY_TURBO_WELCOME_COBORROWER_DECLARATION,
    title        : 'Co-Borrower Questions',
    questions    : getTurboDeclarationQuestions(BorrowerType.COBORROWER),
    showIf       : [hasCoBorrower],
    submitIf     : [isExcludingSsnQuestions],
    submitAction : handleHomeEquityTurboFormSubmit,
    submitText   : 'Submit',
    agreement    : <Disclosure />,
  },
  {
    id           : HOME_EQUITY_TURBO_WELCOME_COLLECT_SSN_FINAL,
    questions    : [homeEquityTurboWelcomeQuestionById(HOME_EQUITY_TURBO_WELCOME_COLLECT_SSN_QUESTION_FINAL)],
    showIf       : [(values) => !isExcludingSsnQuestions(values)],
    submitAction : handleHomeEquityTurboFormSubmit,
    submitText   : 'Submit',
    agreement    : <AuthorizeSsnFullPull />,
  },
  {
    id              : HOME_EQUITY_TURBO_WELCOME_UPLOAD_DOCUMENTS,
    ifNoQuestions   : UploadDocumentsStep,
    hideProgressBar : true,
  },
  {
    id: HOME_EQUITY_MINIMUM_LOAN_STOP_GATE,
    ifNoQuestions: MinimumLoanAmount,
  },
];
